import React from 'react';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { LINE_ITEM_SHIPPING_FEE, propTypes } from '../../util/types';

import css from './OrderBreakdown.module.css';

/**
 * A component that renders the shipping fee as a line item.
 * Only displays shipping fees for single-item purchases.
 * Multi-item shipping fees are handled by LineItemMultiItemMaybe.
 *
 * @component
 * @param {Object} props
 * @param {Array<propTypes.lineItem>} props.lineItems - The line items to render
 * @param {Array} props.allItems - The original items with full listing data (for multi-item purchases)
 * @param {boolean} props.hasFreeShipping - Whether franco threshold is met (free shipping)
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
const LineItemShippingFeeMaybe = props => {
  const { lineItems, hasFreeShipping, intl } = props;

  // Find all shipping fee line items
  const shippingFeeLineItems = lineItems.filter(
    item => (
      (item.code === LINE_ITEM_SHIPPING_FEE || 
       item.code?.startsWith('line-item/shipping-fee')) && 
      !item.reversal
    )
  );

  if (shippingFeeLineItems.length === 0) {
    return null;
  }

  // Check if this is a multi-item purchase with multiple shipping fees
  // If so, these are already handled by LineItemMultiItemMaybe, so don't display them here
  const hasMultiItemShippingFees = shippingFeeLineItems.some(
    item => item.code?.match(/shipping-fee.*listing\d+$/)
  );

  if (hasMultiItemShippingFees) {
    // Multi-item shipping fees are handled by LineItemMultiItemMaybe
    return null;
  }

  // Single shipping fee - display normally or as free if franco threshold met
  const shippingFeeLineItem = shippingFeeLineItems[0];
  const shippingAmount =
    shippingFeeLineItem?.lineTotal?.amount ?? shippingFeeLineItem?.unitPrice?.amount ?? null;
  const isFreeShippingFromLineItem = typeof shippingAmount === 'number' && shippingAmount <= 0;
  const shouldRenderFreeShipping =
    typeof shippingAmount === 'number' ? isFreeShippingFromLineItem : hasFreeShipping;
  
  if (shouldRenderFreeShipping) {
    return (
      <div className={css.lineItem}>
        <span className={css.itemLabel}>
          <FormattedMessage id="OrderBreakdown.shippingFee" />
        </span>
        <span className={css.itemValue} style={{ color: 'var(--colorSuccess)', fontWeight: 'var(--fontWeightSemiBold)' }}>
          <FormattedMessage id="OrderBreakdown.freeShipping" />
        </span>
      </div>
    );
  }
  
  return (
    <div className={css.lineItem}>
      <span className={css.itemLabel}>
        <FormattedMessage id="OrderBreakdown.shippingFee" />
      </span>
      <span className={css.itemValue}>{formatMoney(intl, shippingFeeLineItem.lineTotal)}</span>
    </div>
  );
};

export default LineItemShippingFeeMaybe;
